import * as Y from "yjs";
import { WAVE_FORMAT_YJS_V1, exceedsWaveAppendOpB64Limit, } from "../types.js";
import { WaveCloudAPI } from "../graphql/waveApi.js";
import { WAVE_GRAPHQL } from "../graphql/queries.js";
import { extractNextOpSeqFromSubscriptionPayload, extractWaveOpsFromSubscriptionPayload, } from "../graphql/waveSubscription.js";
import { REMOTE_YJS_ORIGIN, applyYDocUpdateB64, createWaveYDoc, base64ToUint8, encodeDiffUpdateB64, encodeFullYDocSnapshotB64, encodeYDocStateVectorB64, isYjsWaveFormat, } from "./waveYDoc.js";
import { WaveDocEditor } from "./waveDocEditor.js";
import { hydrateYjsFromBlockV1, isBlockV1OpB64 } from "./yjsHydration.js";
import { decodeSnapshotB64 } from "../blocks.js";
import { looksLikePlaceholder } from "../waveHydration.js";
import { cacheEntryBlocksCount, clearWaveDocumentCache, isWaveDocumentCacheUsable, readWaveDocumentCache, writeWaveDocumentCache, } from "../waveDocumentCache.js";
import { materializeBlocksFromYjsSnapshot } from "../waveHydration.js";
import { emptyWaveSyncDiagnostics, expectedAppliedSeq, shouldAlignSentStateVector, shouldClearEmitErrorAfterReconcile, shouldReconcileHealTruncatedCache, shouldSkipPersistDocumentCacheRegression, hasUnconfirmedYjsDiff, hasInboundSeqGap, mergeServerCursors, nextFlushRetryAction, shouldHealPendingStructs, shouldRunSafetyReconcile, } from "../waveSyncDiagnostics.js";
import { emptyWaveSaveStateMeta, } from "../waveSaveState.js";
const EMIT_DEBOUNCE_MS = 50;
/** Parità iOS `cacheWriteDebounceNs`: lo snapshot completo è O(doc), mai per keystroke. */
const CACHE_WRITE_DEBOUNCE_MS = 500;
const POLL_MS = 2500;
const RECONCILE_SAFETY_MS = 15_000;
/** Passive receiver: unconditional reconcile every N safety ticks (~60s). */
const RECONCILE_IDLE_FALLBACK_TICKS = 4;
const FLUSH_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 30_000];
const SYNC_IDLE_POLL_MS = 25;
const SYNC_IDLE_TIMEOUT_MS = 30_000;
function backoffDelayMs(attempt) {
    const index = Math.min(Math.max(attempt, 0), FLUSH_BACKOFF_MS.length - 1);
    const base = FLUSH_BACKOFF_MS[index] ?? 30_000;
    const jitter = 0.75 + Math.random() * 0.5;
    return Math.round(base * jitter);
}
function pendingOpsStorage() {
    if (typeof localStorage !== "undefined")
        return localStorage;
    if (typeof sessionStorage !== "undefined")
        return sessionStorage;
    return null;
}
function pendingYjsOpsStorageKey(waveId) {
    return `wave:yjs-pending:${waveId}`;
}
/** Yjs empty diff is 2 bytes (`AAA=`); only push when the client has structs the server lacks. */
function isNonEmptyYjsUpdateB64(b64) {
    return base64ToUint8(b64).length > 2;
}
export class WaveYjsProvider {
    cfg;
    doc;
    _editor;
    waveId = null;
    appliedSeq = 0;
    seenOpIds = new Set();
    emitTimer = null;
    pendingEmit = false;
    unsubscribeRealtime = null;
    pollTimer = null;
    reconcileTimer = null;
    lastEmitError = null;
    onStatusChange;
    onEmitError;
    onLocalHydrated;
    realtime;
    pendingOps = [];
    emitDraining = false;
    isFlushingLocal = false;
    flushChain = Promise.resolve();
    drainChain = Promise.resolve();
    hydrationChain = Promise.resolve();
    reconcileInFlight = false;
    reconcileHealUsed = false;
    /** Block count when local cache was applied at connect (for truncated-merge heal). */
    cacheBlocksAtApply = null;
    lastSentStateVectorB64 = null;
    wasRealtimeConnected = false;
    waveSnapshotSeq = 0;
    waveNextOpSeq = 0;
    waveFormatVersion = null;
    networkHydrating = false;
    networkHydrationListeners = new Set();
    diagnostics = emptyWaveSyncDiagnostics();
    diagnosticsListeners = new Set();
    saveState = "synced";
    saveStateMeta = emptyWaveSaveStateMeta();
    onSaveStateChange;
    browserOnline = typeof navigator === "undefined" ? true : navigator.onLine;
    pendingDrainTimer = null;
    pendingDrainAttempt = 0;
    flushRetryTimer = null;
    flushRetryAttempt = 0;
    cacheWriteTimer = null;
    lastAckAt = null;
    editorListeners = new Set();
    editorBridgeUnsub = null;
    safetyReconcileTick = 0;
    constructor(cfg, realtime, opts) {
        this.cfg = cfg;
        this.doc = createWaveYDoc();
        this._editor = new WaveDocEditor(this.doc);
        this.onStatusChange = opts?.onStatusChange;
        this.onEmitError = opts?.onEmitError;
        this.onLocalHydrated = opts?.onLocalHydrated;
        this.onSaveStateChange = opts?.onSaveStateChange;
        this.realtime = realtime;
        this.doc.on("update", this.handleDocUpdate);
        this.wireEditorBridge();
    }
    wireEditorBridge() {
        this.editorBridgeUnsub?.();
        this.editorBridgeUnsub = this._editor.subscribe(() => {
            for (const listener of this.editorListeners)
                listener();
        });
    }
    get editor() {
        return this._editor;
    }
    get blocks() {
        return this.editor.blocks;
    }
    get contentRevision() {
        return this.editor.contentRevision;
    }
    get appliedOpSeq() {
        return this.appliedSeq;
    }
    getLastEmitError() {
        return this.lastEmitError;
    }
    getSaveState() {
        return this.saveState;
    }
    getSaveStateMeta() {
        return { ...this.saveStateMeta };
    }
    /** Called when browser connectivity returns (window `online`). */
    notifyBrowserOnline() {
        this.browserOnline = true;
        this.recomputeSaveState();
        this.clearPendingDrainTimer();
        this.pendingDrainAttempt = 0;
        this.clearFlushRetryTimer();
        this.flushRetryAttempt = 0;
        if (this.pendingOps.length > 0 || this.pendingEmit) {
            void this.enqueueDrain();
            void this.flushLocalUpdateNow();
        }
    }
    /** Called when browser goes offline (window `offline`). */
    notifyBrowserOffline() {
        this.browserOnline = false;
        this.recomputeSaveState();
    }
    hasUnconfirmedLocalDiff() {
        const currentSv = encodeYDocStateVectorB64(this.doc);
        // Short-circuit caso comune (synced): SV identico, niente encode diff O(doc).
        if (this.lastSentStateVectorB64 != null &&
            this.lastSentStateVectorB64 === currentSv) {
            return false;
        }
        const diffB64 = encodeDiffUpdateB64(this.doc, this.lastSentStateVectorB64);
        return hasUnconfirmedYjsDiff({
            docStateVectorB64: currentSv,
            lastSentStateVectorB64: this.lastSentStateVectorB64,
            diffByteLength: base64ToUint8(diffB64).length,
        });
    }
    recomputeSaveState() {
        const pendingOutboundCount = this.pendingOps.length;
        const hasLocalDiff = this.pendingEmit ||
            this.hasUnconfirmedLocalDiff() ||
            pendingOutboundCount > 0;
        const activelySending = this.isFlushingLocal ||
            this.emitDraining ||
            this.emitTimer != null;
        let next = "synced";
        if (this.lastEmitError && hasLocalDiff) {
            next = "error";
        }
        else if (activelySending && this.browserOnline) {
            next = "syncing";
        }
        else if (hasLocalDiff) {
            next = this.browserOnline ? "syncing" : "pending";
        }
        const meta = {
            pendingOutboundCount,
            lastAckAt: this.lastAckAt,
            lastError: this.lastEmitError,
        };
        if (this.saveState === next &&
            this.saveStateMeta.pendingOutboundCount === meta.pendingOutboundCount &&
            this.saveStateMeta.lastAckAt === meta.lastAckAt &&
            this.saveStateMeta.lastError === meta.lastError) {
            return;
        }
        this.saveState = next;
        this.saveStateMeta = meta;
        this.onSaveStateChange?.(next, meta);
    }
    subscribe(listener) {
        this.editorListeners.add(listener);
        return () => this.editorListeners.delete(listener);
    }
    isNetworkHydrating() {
        return this.networkHydrating;
    }
    subscribeNetworkHydration(listener) {
        this.networkHydrationListeners.add(listener);
        return () => this.networkHydrationListeners.delete(listener);
    }
    getSyncDiagnostics() {
        return { ...this.diagnostics };
    }
    subscribeSyncDiagnostics(listener) {
        this.diagnosticsListeners.add(listener);
        return () => this.diagnosticsListeners.delete(listener);
    }
    patchDiagnostics(patch) {
        const expected = expectedAppliedSeq({
            latestSnapshotSeq: this.waveSnapshotSeq,
            nextOpSeq: this.waveNextOpSeq,
        });
        this.diagnostics = {
            ...this.diagnostics,
            ...patch,
            waveId: this.waveId,
            formatVersion: this.waveFormatVersion,
            waveSnapshotSeq: this.waveSnapshotSeq,
            waveNextOpSeq: this.waveNextOpSeq,
            appliedSeq: this.appliedSeq,
            expectedLastSeq: expected,
            seqGap: Math.max(0, expected - this.appliedSeq),
            blocksCount: this.blocks.length,
            networkHydrating: this.networkHydrating,
            updatedAt: Date.now(),
        };
        for (const listener of this.diagnosticsListeners)
            listener();
    }
    emitDiagnostics(phase) {
        this.patchDiagnostics(phase ? { lastPhase: phase } : {});
    }
    setNetworkHydrating(value) {
        if (this.networkHydrating === value)
            return;
        this.networkHydrating = value;
        for (const listener of this.networkHydrationListeners)
            listener();
        this.emitDiagnostics(value ? "network-hydrating" : "network-ready");
    }
    logSync(event, detail) {
        if (typeof console !== "undefined") {
            console.info(`[Wave Yjs] ${event}`, detail ?? "");
        }
    }
    // Niente encode del diff qui: O(doc) per ogni riga di log su documenti grandi.
    syncDebugDetail(phase) {
        return [
            `phase=${phase}`,
            `blocks=${this.blocks.length}`,
            `appliedSeq=${this.appliedSeq}`,
            `pendingOps=${this.pendingOps.length}`,
            `pendingEmit=${this.pendingEmit}`,
            `saveState=${this.saveState}`,
            `hydrating=${this.networkHydrating}`,
        ].join(" ");
    }
    hasOutboundWork() {
        return (this.pendingEmit ||
            this.hasUnconfirmedLocalDiff() ||
            this.pendingOps.length > 0);
    }
    maybeClearEmitErrorAfterReconcile() {
        if (shouldClearEmitErrorAfterReconcile({
            hasUnconfirmedLocalDiff: this.hasUnconfirmedLocalDiff(),
            pendingOpsCount: this.pendingOps.length,
            pendingEmit: this.pendingEmit,
        })) {
            this.setEmitError(null);
        }
    }
    /** Advance lastSentStateVector only when cloud has acked all local structs. */
    alignSentStateVectorIfSynced(phase) {
        if (!shouldAlignSentStateVector({
            hasUnconfirmedLocalDiff: this.hasUnconfirmedLocalDiff(),
            pendingOpsCount: this.pendingOps.length,
            pendingEmit: this.pendingEmit,
        })) {
            this.logSync("sent-state skip", this.syncDebugDetail(phase));
            return;
        }
        this.lastSentStateVectorB64 = encodeYDocStateVectorB64(this.doc);
        this.logSync("sent-state aligned", this.syncDebugDetail(phase));
    }
    /** After inbound remote ops / reconcile pull — doc matches cloud; reset save baseline (iOS parity). */
    alignSentStateVectorAfterInboundSync(phase) {
        if (this.pendingEmit ||
            this.pendingOps.length > 0 ||
            this.isFlushingLocal ||
            this.emitDraining) {
            return;
        }
        this.lastSentStateVectorB64 = encodeYDocStateVectorB64(this.doc);
        this.lastAckAt = Date.now();
        this.logSync("sent-state inbound align", this.syncDebugDetail(phase));
        this.recomputeSaveState();
    }
    enqueueHydrationStep(step) {
        this.hydrationChain = this.hydrationChain
            .then(step)
            .catch(() => undefined);
        return this.hydrationChain;
    }
    /** Push local edits not yet acknowledged by the server (uses lastSentStateVectorB64). */
    async pushUnsentLocalStateIfNeeded(phase) {
        if (!this.waveId)
            return phase;
        const pushB64 = encodeDiffUpdateB64(this.doc, this.lastSentStateVectorB64);
        // SV catturato insieme al diff: edit arrivati durante l'await non sono nel push.
        const stateVectorAtPush = encodeYDocStateVectorB64(this.doc);
        if (!isNonEmptyYjsUpdateB64(pushB64))
            return phase;
        try {
            const usedSnapshot = await this.pushDiffToCloud(pushB64);
            this.lastSentStateVectorB64 = stateVectorAtPush;
            this.persistDocumentCache();
            this.logSync(usedSnapshot ? "reconcile push snapshot" : "reconcile push", `blocks=${this.blocks.length}`);
            return usedSnapshot ? "reconcile-push-snapshot" : "reconcile-push";
        }
        catch (err) {
            const message = err instanceof Error ? err.message : "reconcile push failed";
            this.setEmitError(message);
            this.pendingEmit = true;
            this.logSync("reconcile push failed", message);
            return phase;
        }
    }
    /** Returns true when a full snapshot was published instead of append ops. */
    async pushDiffToCloud(diffB64) {
        if (!this.waveId)
            return false;
        if (!exceedsWaveAppendOpB64Limit(diffB64)) {
            await this.appendOpsB64([diffB64]);
            return false;
        }
        const seq = this.appliedSeq;
        const snap = encodeFullYDocSnapshotB64(this.doc);
        await WaveCloudAPI.publishSnapshot(this.cfg, this.waveId, snap, seq, isYjsWaveFormat(this.waveFormatVersion) ? WAVE_FORMAT_YJS_V1 : undefined);
        this.appliedSeq = Math.max(this.appliedSeq, seq);
        this.waveSnapshotSeq = Math.max(this.waveSnapshotSeq, seq);
        return true;
    }
    handleDocUpdate = (_update, origin) => {
        if (origin === REMOTE_YJS_ORIGIN)
            return;
        this.persistDocumentCache();
        this.scheduleEmit();
    };
    scheduleEmit() {
        this.pendingEmit = true;
        this.recomputeSaveState();
        if (this.emitTimer)
            clearTimeout(this.emitTimer);
        this.emitTimer = setTimeout(() => {
            this.emitTimer = null;
            void this.enqueueFlush();
        }, EMIT_DEBOUNCE_MS);
    }
    /** Flush debounced local Yjs diff immediately (e.g. after adding a block). */
    async flushLocalUpdateNow() {
        if (this.emitTimer) {
            clearTimeout(this.emitTimer);
            this.emitTimer = null;
        }
        this.pendingEmit = true;
        await this.enqueueFlush();
        await this.waitForSyncIdle();
    }
    enqueueFlush() {
        this.flushChain = this.flushChain
            .then(() => this.flushLocalUpdate())
            .catch(() => undefined);
        return this.flushChain;
    }
    enqueueDrain() {
        this.drainChain = this.drainChain
            .then(() => this.drainEmit())
            .catch(() => undefined);
        return this.drainChain;
    }
    async waitForSyncIdle() {
        const deadline = Date.now() + SYNC_IDLE_TIMEOUT_MS;
        while (Date.now() < deadline) {
            if (this.emitTimer) {
                clearTimeout(this.emitTimer);
                this.emitTimer = null;
                this.pendingEmit = true;
                await this.enqueueFlush();
            }
            const idle = !this.pendingEmit &&
                !this.isFlushingLocal &&
                !this.emitDraining &&
                !this.pendingDrainTimer &&
                this.pendingOps.length === 0 &&
                !this.hasUnconfirmedLocalDiff();
            if (idle)
                return;
            await new Promise((resolve) => setTimeout(resolve, SYNC_IDLE_POLL_MS));
        }
    }
    async flushLocalUpdate() {
        if (!this.waveId || !this.pendingEmit)
            return;
        this.pendingEmit = false;
        this.isFlushingLocal = true;
        try {
            const diffB64 = encodeDiffUpdateB64(this.doc, this.lastSentStateVectorB64);
            // SV corrispondente al diff appena codificato (parità iOS `stateVectorAtEmit`):
            // mai avanzare lastSent oltre ciò che è stato davvero spedito, altrimenti i
            // caratteri digitati durante il volo della richiesta non vengono più inviati
            // e i receiver restano in pending structs fino al reconcile.
            const stateVectorAtEmit = encodeYDocStateVectorB64(this.doc);
            const diffBytes = base64ToUint8(diffB64).length;
            if (!isNonEmptyYjsUpdateB64(diffB64)) {
                this.alignSentStateVectorIfSynced("flush-empty-diff");
                this.recomputeSaveState();
                this.persistDocumentCache();
                return;
            }
            this.logSync("flush push", this.syncDebugDetail(`diffBytes=${diffBytes}`));
            try {
                await this.pushDiffToCloud(diffB64);
                if (this.pendingOps.length === 0 &&
                    !this.pendingEmit) {
                    this.alignSentStateVectorAfterInboundSync("flush-ack");
                }
                else {
                    this.lastSentStateVectorB64 = stateVectorAtEmit;
                    this.recomputeSaveState();
                }
                this.persistPendingYjsOps();
                this.persistDocumentCache();
                this.logSync("flush ack", this.syncDebugDetail("post-ack"));
            }
            catch (err) {
                const message = err instanceof Error ? err.message : "Sync wave non riuscita";
                this.setEmitError(message);
                this.pendingEmit = true;
                this.persistPendingYjsOps();
                this.persistDocumentCache();
                this.logSync("flush failed", `${message} ${this.syncDebugDetail("error")}`);
            }
        }
        finally {
            this.isFlushingLocal = false;
            this.recomputeSaveState();
            const action = nextFlushRetryAction({
                pendingEmit: this.pendingEmit,
                hasEmitError: this.lastEmitError != null,
                browserOnline: this.browserOnline,
            });
            if (action === "flush-now") {
                await this.flushLocalUpdate();
            }
            else if (action === "retry-backoff") {
                this.scheduleFlushRetry();
            }
            // wait-online: notifyBrowserOnline riavvia il flush al rientro rete.
        }
    }
    clearFlushRetryTimer() {
        if (this.flushRetryTimer) {
            clearTimeout(this.flushRetryTimer);
            this.flushRetryTimer = null;
        }
    }
    scheduleFlushRetry() {
        if (this.flushRetryTimer)
            return;
        const attempt = this.flushRetryAttempt;
        this.flushRetryAttempt = attempt + 1;
        this.flushRetryTimer = setTimeout(() => {
            this.flushRetryTimer = null;
            this.pendingEmit = true;
            void this.enqueueFlush();
        }, backoffDelayMs(attempt));
    }
    persistPendingYjsOps() {
        const storage = pendingOpsStorage();
        if (!storage || !this.waveId)
            return;
        const key = pendingYjsOpsStorageKey(this.waveId);
        if (!this.pendingOps.length) {
            storage.removeItem(key);
            if (typeof sessionStorage !== "undefined") {
                sessionStorage.removeItem(key);
            }
            return;
        }
        try {
            storage.setItem(key, JSON.stringify(this.pendingOps));
        }
        catch {
            /* quota / private mode */
        }
    }
    restorePendingYjsOps(waveId) {
        const key = pendingYjsOpsStorageKey(waveId);
        let raw = null;
        const storage = pendingOpsStorage();
        if (storage) {
            raw = storage.getItem(key);
            if (raw)
                storage.removeItem(key);
        }
        if (!raw && typeof sessionStorage !== "undefined") {
            raw = sessionStorage.getItem(key);
            if (raw)
                sessionStorage.removeItem(key);
        }
        if (!raw)
            return;
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) {
                this.pendingOps.push(...parsed);
                this.recomputeSaveState();
            }
        }
        catch {
            /* ignore */
        }
    }
    clearPendingDrainTimer() {
        if (this.pendingDrainTimer) {
            clearTimeout(this.pendingDrainTimer);
            this.pendingDrainTimer = null;
        }
    }
    schedulePendingDrainRetry() {
        this.clearPendingDrainTimer();
        const attempt = this.pendingDrainAttempt + 1;
        this.pendingDrainAttempt = attempt;
        this.pendingDrainTimer = setTimeout(() => {
            this.pendingDrainTimer = null;
            void this.enqueueDrain();
        }, backoffDelayMs(attempt - 1));
    }
    resetDoc() {
        this.doc.off("update", this.handleDocUpdate);
        this.doc.destroy();
        this.doc = createWaveYDoc();
        this._editor = new WaveDocEditor(this.doc);
        this.doc.on("update", this.handleDocUpdate);
        this.wireEditorBridge();
        for (const listener of this.editorListeners)
            listener();
    }
    updateServerCursors(patch) {
        const merged = mergeServerCursors({
            snapshotSeq: this.waveSnapshotSeq,
            nextOpSeq: this.waveNextOpSeq,
        }, patch);
        if (merged.snapshotSeq !== this.waveSnapshotSeq ||
            merged.nextOpSeq !== this.waveNextOpSeq) {
            this.waveSnapshotSeq = merged.snapshotSeq;
            this.waveNextOpSeq = merged.nextOpSeq;
            this.emitDiagnostics("server-cursors");
        }
    }
    /** Server-authoritative Yjs reconciliation (heals seq gaps and pendingStructs). */
    async reconcileWithServer(options) {
        if (!this.waveId || this.reconcileInFlight)
            return;
        this.reconcileInFlight = true;
        try {
            if (this.emitTimer) {
                clearTimeout(this.emitTimer);
                this.emitTimer = null;
            }
            await this.flushChain;
            await this.drainChain;
            let attempt = 0;
            let replaceDoc = options?.replaceDoc ?? false;
            let revisionBefore = this.contentRevision;
            while (attempt < 3) {
                attempt += 1;
                if (replaceDoc) {
                    if (this.hasUnconfirmedLocalDiff()) {
                        await this.pushUnsentLocalStateIfNeeded("pre-replace-push");
                    }
                    const seq = this.appliedSeq;
                    this.resetDoc();
                    this.appliedSeq = seq;
                    this.lastSentStateVectorB64 = encodeYDocStateVectorB64(this.doc);
                    this.logSync("reconcile replace reset", this.syncDebugDetail("replace-doc"));
                }
                const sv = replaceDoc ? null : encodeYDocStateVectorB64(this.doc);
                const result = await WaveCloudAPI.getWaveYjsSync(this.cfg, this.waveId, sv);
                const updateBytes = result.updateB64?.length ?? 0;
                if (result.updateB64?.trim()) {
                    applyYDocUpdateB64(this.doc, result.updateB64, REMOTE_YJS_ORIGIN);
                    this.logSync(replaceDoc ? "reconcile full replace" : "reconcile applied", `bytes=${updateBytes} ${this.syncDebugDetail("pull")}`);
                }
                // Parity with iOS: server state vector is authoritative after pull —
                // do not re-push remote structs the client just applied (anti-storm).
                this.lastSentStateVectorB64 = result.serverStateVectorB64?.trim()
                    ? result.serverStateVectorB64
                    : encodeYDocStateVectorB64(this.doc);
                this.appliedSeq = Math.max(this.appliedSeq, result.snapshotSeq);
                this.updateServerCursors({
                    snapshotSeq: result.snapshotSeq,
                    nextOpSeq: result.nextOpSeq,
                });
                this.maybeClearEmitErrorAfterReconcile();
                let lastPhase = replaceDoc ? "reconcile-replace" : "reconcile";
                if (result.serverStateVectorB64?.trim() && this.hasUnconfirmedLocalDiff()) {
                    lastPhase = await this.pushUnsentLocalStateIfNeeded(lastPhase);
                }
                this.patchDiagnostics({
                    lastPhase,
                    lastReconcileUpdateBytes: updateBytes,
                });
                this.logSync("reconcile ok", `attempt=${attempt} replace=${replaceDoc} snapshotSeq=${result.snapshotSeq} appliedSeq=${this.appliedSeq} blocks=${this.blocks.length} phase=${lastPhase}`);
                const blocksAfter = this.blocks.length;
                if (shouldReconcileHealTruncatedCache({
                    cacheBlocksAtApply: this.cacheBlocksAtApply,
                    reconcileHealUsed: this.reconcileHealUsed,
                    replaceDoc,
                    attempt,
                    updateBytes,
                    blocksAfter,
                })) {
                    this.reconcileHealUsed = true;
                    replaceDoc = true;
                    revisionBefore = this.contentRevision;
                    this.logSync("reconcile heal", "truncated cache merge — replacing doc from authoritative server state");
                    continue;
                }
                if (this.reconcileHealUsed && replaceDoc) {
                    break;
                }
                const revisionChanged = this.contentRevision !== revisionBefore;
                if (!revisionChanged && !this.pendingEmit)
                    break;
                this.pendingEmit = false;
                revisionBefore = this.contentRevision;
            }
            this.patchDiagnostics({ cacheApplied: false });
            this.cacheBlocksAtApply = null;
            this.persistDocumentCacheNow();
            if (!this.pendingEmit &&
                this.pendingOps.length === 0) {
                if (!this.lastEmitError) {
                    this.alignSentStateVectorAfterInboundSync("reconcile-done");
                }
                else {
                    this.alignSentStateVectorIfSynced("reconcile-done");
                    this.recomputeSaveState();
                }
            }
            else {
                this.logSync("reconcile pending outbound", this.syncDebugDetail("done"));
                this.recomputeSaveState();
            }
        }
        catch (err) {
            const message = err instanceof Error ? err.message : "Riconciliazione Yjs non riuscita";
            this.setEmitError(message);
            this.logSync("reconcile failed", message);
        }
        finally {
            this.reconcileInFlight = false;
        }
    }
    scheduleReconcile() {
        void this.reconcileWithServer();
    }
    detectSeqGap(sorted) {
        return hasInboundSeqGap({
            appliedSeq: this.appliedSeq,
            sortedOps: sorted,
            seenOpIds: this.seenOpIds,
        });
    }
    applyBlockV1History(wave, blockOps) {
        const jsonSnap = decodeSnapshotB64(wave.latestSnapshotB64);
        const baseline = jsonSnap && jsonSnap.version === 1 ? jsonSnap : { blocks: [], version: 1 };
        const bytes = hydrateYjsFromBlockV1(baseline, blockOps);
        Y.applyUpdate(this.doc, bytes, REMOTE_YJS_ORIGIN);
        for (const op of blockOps) {
            this.seenOpIds.add(op.opId);
            this.appliedSeq = Math.max(this.appliedSeq, op.seq);
        }
        this.lastSentStateVectorB64 = encodeYDocStateVectorB64(this.doc);
    }
    /** Conta i blocchi dello snapshot server una sola volta per connect (materializzazione O(doc)). */
    countServerSnapshotBlocks(wave) {
        if (!isYjsWaveFormat(wave.formatVersion) || !wave.latestSnapshotB64?.trim()) {
            return null;
        }
        return materializeBlocksFromYjsSnapshot(wave.latestSnapshotB64).length;
    }
    applyLocalDocumentCache(wave, serverBlocksCount) {
        const cached = readWaveDocumentCache(wave.waveId);
        if (!cached?.snapshotB64?.trim())
            return;
        const snapshotSeq = wave.latestSnapshotSeq ?? 0;
        const serverSnapB64 = isYjsWaveFormat(wave.formatVersion)
            ? wave.latestSnapshotB64
            : null;
        if (!isWaveDocumentCacheUsable(cached, snapshotSeq, serverSnapB64, serverBlocksCount)) {
            const cacheBlocks = cacheEntryBlocksCount(cached);
            const serverBlocks = serverSnapB64 ? serverBlocksCount : null;
            const incomplete = cached.appliedSeq >= snapshotSeq &&
                serverBlocks != null &&
                cacheBlocks < serverBlocks;
            if (incomplete) {
                clearWaveDocumentCache(wave.waveId);
            }
            this.patchDiagnostics({
                cacheRejectedStale: !incomplete,
                cacheApplied: false,
                cacheAppliedSeq: cached.appliedSeq,
                lastPhase: incomplete ? "cache-rejected-incomplete" : "cache-rejected",
            });
            this.logSync(incomplete ? "cache rejected (incomplete)" : "cache rejected (stale)", `cacheAppliedSeq=${cached.appliedSeq} snapshotSeq=${snapshotSeq} cacheBlocks=${cacheBlocks}${serverBlocks != null ? ` serverBlocks=${serverBlocks}` : ""}`);
            return;
        }
        applyYDocUpdateB64(this.doc, cached.snapshotB64, REMOTE_YJS_ORIGIN);
        this.appliedSeq = Math.max(this.appliedSeq, cached.appliedSeq);
        this.cacheBlocksAtApply = cacheEntryBlocksCount(cached);
        this.patchDiagnostics({
            cacheApplied: true,
            cacheAppliedSeq: cached.appliedSeq,
            cacheRejectedStale: false,
            lastPhase: "cache-applied",
        });
        this.logSync("cache applied", `appliedSeq=${cached.appliedSeq}`);
    }
    applyLocalSnapshot(wave, serverBlocksCount) {
        if (!isYjsWaveFormat(wave.formatVersion) || !wave.latestSnapshotB64?.trim()) {
            return;
        }
        const snapshotSeq = wave.latestSnapshotSeq ?? 0;
        const serverBlockCount = serverBlocksCount ??
            materializeBlocksFromYjsSnapshot(wave.latestSnapshotB64).length;
        const cached = readWaveDocumentCache(wave.waveId);
        if (cached?.snapshotB64?.trim()) {
            const cacheBlockCount = cacheEntryBlocksCount(cached);
            const cacheUsable = isWaveDocumentCacheUsable(cached, snapshotSeq, wave.latestSnapshotB64, serverBlockCount);
            const cacheAhead = cacheUsable &&
                (cacheBlockCount > serverBlockCount || cached.appliedSeq > snapshotSeq);
            if (cacheAhead) {
                this.appliedSeq = Math.max(this.appliedSeq, snapshotSeq);
                this.patchDiagnostics({ lastPhase: "snapshot-skipped-cache-ahead" });
                this.logSync("snapshot skipped (cache ahead)", `cacheBlocks=${cacheBlockCount} serverBlocks=${serverBlockCount} cacheSeq=${cached.appliedSeq} snapshotSeq=${snapshotSeq}`);
                return;
            }
        }
        applyYDocUpdateB64(this.doc, wave.latestSnapshotB64, REMOTE_YJS_ORIGIN);
        this.appliedSeq = Math.max(this.appliedSeq, snapshotSeq);
        this.patchDiagnostics({ lastPhase: "snapshot-applied" });
        this.logSync("snapshot applied", `snapshotSeq=${snapshotSeq} blocks=${this.blocks.length}`);
    }
    async finishNetworkHydration(wave) {
        await this.enqueueHydrationStep(async () => {
            try {
                const tailAfterSeq = this.appliedSeq;
                const tailOps = await WaveCloudAPI.listAllOps(this.cfg, wave.waveId, tailAfterSeq);
                this.logSync("tail ops fetched", `afterSeq=${tailAfterSeq} count=${tailOps.length} nextOpSeq=${wave.nextOpSeq ?? 0}`);
                let tailStats = { applied: 0, skippedLegacy: 0, failed: 0 };
                if (tailOps.length > 0) {
                    tailStats = this.applyRemoteOpsFast(tailOps);
                }
                this.patchDiagnostics({
                    tailRequestedAfterSeq: tailAfterSeq,
                    lastTailOpsFetched: tailOps.length,
                    lastTailOpsApplied: tailStats.applied,
                    lastTailOpsSkippedLegacy: tailStats.skippedLegacy,
                    lastTailOpsFailed: tailStats.failed,
                    lastPhase: "tail-hydration",
                });
                this.logSync("tail hydration done", `applied=${tailStats.applied} skippedLegacy=${tailStats.skippedLegacy} failed=${tailStats.failed} appliedSeq=${this.appliedSeq} ${this.syncDebugDetail("tail")}`);
                if (looksLikePlaceholder(this.blocks, wave)) {
                    const allOps = await WaveCloudAPI.listAllOps(this.cfg, wave.waveId, 0);
                    if (allOps.length > 0) {
                        this.resetDoc();
                        this.seenOpIds.clear();
                        this.appliedSeq = 0;
                        const recoveryServerBlocks = this.countServerSnapshotBlocks(wave);
                        this.applyLocalDocumentCache(wave, recoveryServerBlocks);
                        this.applyLocalSnapshot(wave, recoveryServerBlocks);
                        this.lastSentStateVectorB64 = encodeYDocStateVectorB64(this.doc);
                        const blockOps = allOps.filter((op) => isBlockV1OpB64(op.dataB64));
                        const yjsOps = allOps.filter((op) => !isBlockV1OpB64(op.dataB64));
                        if (blockOps.length > 0) {
                            this.applyBlockV1History(wave, blockOps);
                        }
                        if (yjsOps.length > 0) {
                            const recoveryStats = this.applyRemoteOpsFast(yjsOps);
                            this.patchDiagnostics({
                                lastTailOpsFetched: yjsOps.length,
                                lastTailOpsApplied: recoveryStats.applied,
                                lastTailOpsSkippedLegacy: recoveryStats.skippedLegacy,
                                lastTailOpsFailed: recoveryStats.failed,
                                lastPhase: "block-v1-recovery",
                            });
                        }
                    }
                }
                else if (!isYjsWaveFormat(wave.formatVersion) &&
                    this.blocks.length === 0) {
                    const allOps = await WaveCloudAPI.listAllOps(this.cfg, wave.waveId, 0);
                    const blockOps = allOps.filter((op) => isBlockV1OpB64(op.dataB64));
                    if (blockOps.length > 0) {
                        this.applyBlockV1History(wave, blockOps);
                        const yjsOps = allOps.filter((op) => !isBlockV1OpB64(op.dataB64));
                        if (yjsOps.length > 0) {
                            const recoveryStats = this.applyRemoteOpsFast(yjsOps);
                            this.patchDiagnostics({
                                lastTailOpsFetched: yjsOps.length,
                                lastTailOpsApplied: recoveryStats.applied,
                                lastTailOpsSkippedLegacy: recoveryStats.skippedLegacy,
                                lastTailOpsFailed: recoveryStats.failed,
                                lastPhase: "block-v1-recovery",
                            });
                        }
                    }
                }
                this.restorePendingYjsOps(wave.waveId);
                if (this.pendingOps.length > 0) {
                    await this.enqueueDrain();
                }
                await this.flushLocalUpdateNow();
                this.alignSentStateVectorIfSynced("hydration-flush");
                this.persistDocumentCacheNow();
                this.startBackgroundSync(wave.waveId);
            }
            catch (err) {
                const message = err instanceof Error ? err.message : "Network hydration failed";
                this.logSync("network hydration failed", `${message} ${this.syncDebugDetail("hydration-error")}`);
                this.startBackgroundSync(wave.waveId);
            }
            finally {
                this.setNetworkHydrating(false);
            }
        });
    }
    /** Write debounced (coalesca handleDocUpdate + notifyDocumentMaterialized per keystroke). */
    persistDocumentCache() {
        if (!this.waveId)
            return;
        if (this.cacheWriteTimer)
            return;
        this.cacheWriteTimer = setTimeout(() => {
            this.cacheWriteTimer = null;
            this.persistDocumentCacheNow();
        }, CACHE_WRITE_DEBOUNCE_MS);
    }
    clearCacheWriteTimer() {
        if (this.cacheWriteTimer) {
            clearTimeout(this.cacheWriteTimer);
            this.cacheWriteTimer = null;
        }
    }
    persistDocumentCacheNow() {
        this.clearCacheWriteTimer();
        if (!this.waveId)
            return;
        const blocksCount = this.blocks.length;
        const cached = readWaveDocumentCache(this.waveId);
        const cachedBlocksCount = cached?.snapshotB64?.trim()
            ? cacheEntryBlocksCount(cached)
            : null;
        if (shouldSkipPersistDocumentCacheRegression({
            blocksCount,
            cachedBlocksCount,
            hasOutboundWork: this.hasOutboundWork(),
        })) {
            this.logSync("cache persist skip", `blocks=${blocksCount} cachedBlocks=${cachedBlocksCount ?? "—"}`);
            return;
        }
        writeWaveDocumentCache(this.waveId, {
            snapshotB64: encodeFullYDocSnapshotB64(this.doc),
            appliedSeq: this.appliedSeq,
            snapshotSeq: this.waveSnapshotSeq,
            blocksCount,
            savedAt: Date.now(),
        });
    }
    startBackgroundSync(waveId) {
        void this.reconcileWithServer()
            .then(() => this.startRealtime(waveId))
            .catch((err) => {
            const message = err instanceof Error ? err.message : "Background sync failed";
            this.logSync("background sync failed", message);
            void this.startRealtime(waveId);
        });
    }
    async connect(wave) {
        this.resetDoc();
        this.waveId = wave.waveId;
        this.waveSnapshotSeq = wave.latestSnapshotSeq ?? 0;
        this.waveNextOpSeq = wave.nextOpSeq ?? 0;
        this.waveFormatVersion = wave.formatVersion ?? null;
        this.appliedSeq = 0;
        this.seenOpIds.clear();
        this.diagnostics = emptyWaveSyncDiagnostics();
        this.reconcileHealUsed = false;
        this.cacheBlocksAtApply = null;
        this.setNetworkHydrating(true);
        const serverBlocksCount = this.countServerSnapshotBlocks(wave);
        this.applyLocalDocumentCache(wave, serverBlocksCount);
        this.applyLocalSnapshot(wave, serverBlocksCount);
        this.lastSentStateVectorB64 = encodeYDocStateVectorB64(this.doc);
        this.logSync("local hydrated", this.syncDebugDetail("connect"));
        this.emitDiagnostics("local-hydrated");
        this.onLocalHydrated?.();
        void this.finishNetworkHydration(wave);
    }
    async reloadFromCloud(wave) {
        this.disconnect();
        await this.connect(wave);
    }
    /**
     * Synchronous teardown for full-page navigation (Safari refresh).
     * Persists local state only — no network flush (avoids hang on pagehide).
     */
    prepareForUnload() {
        if (this.emitTimer) {
            clearTimeout(this.emitTimer);
            this.emitTimer = null;
        }
        this.clearPendingDrainTimer();
        this.clearFlushRetryTimer();
        this.persistPendingYjsOps();
        this.persistDocumentCacheNow();
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
        if (this.reconcileTimer) {
            clearInterval(this.reconcileTimer);
            this.reconcileTimer = null;
        }
        this.updatePollingFallbackFlag();
    }
    disconnect() {
        this.setNetworkHydrating(false);
        this.clearPendingDrainTimer();
        this.clearFlushRetryTimer();
        void this.flushLocalUpdateNow();
        this.persistPendingYjsOps();
        this.persistDocumentCacheNow();
        if (this.emitTimer) {
            clearTimeout(this.emitTimer);
            this.emitTimer = null;
        }
        this.unsubscribeRealtime?.();
        this.unsubscribeRealtime = null;
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
        if (this.reconcileTimer) {
            clearInterval(this.reconcileTimer);
            this.reconcileTimer = null;
        }
        this.updatePollingFallbackFlag();
        this.waveId = null;
        this.wasRealtimeConnected = false;
    }
    /** Best-effort fast path (subscription / poll); reconcile heals gaps. */
    applyRemoteOps(ops) {
        if (ops.length) {
            const maxSeq = Math.max(...ops.map((o) => o.seq));
            this.updateServerCursors({ maxAppliedSeq: maxSeq });
        }
        const revisionBefore = this.contentRevision;
        const result = this.applyRemoteOpsFast(ops);
        const pendingStructs = shouldHealPendingStructs({
            yjsOpsApplied: result.applied,
            revisionBefore,
            revisionAfter: this.contentRevision,
        });
        if (result.needReconcile || pendingStructs) {
            if (pendingStructs) {
                this.logSync("pending structs", `applied=${result.applied} revision=${revisionBefore}`);
            }
            this.scheduleReconcile();
        }
        else {
            this.alignSentStateVectorAfterInboundSync("remote-ops");
        }
        this.patchDiagnostics({ lastPhase: "remote-ops" });
    }
    applyRemoteOpsFast(ops) {
        const sorted = [...ops].sort((a, b) => a.seq - b.seq);
        if (this.detectSeqGap(sorted)) {
            this.logSync("seq gap detected", sorted.map((o) => o.seq).join(","));
            return { needReconcile: true, applied: 0, skippedLegacy: 0, failed: 0 };
        }
        let needReconcile = false;
        let applied = 0;
        let skippedLegacy = 0;
        let failed = 0;
        for (const op of sorted) {
            if (this.seenOpIds.has(op.opId))
                continue;
            if (isBlockV1OpB64(op.dataB64)) {
                this.seenOpIds.add(op.opId);
                this.appliedSeq = Math.max(this.appliedSeq, op.seq);
                skippedLegacy += 1;
                continue;
            }
            try {
                applyYDocUpdateB64(this.doc, op.dataB64, REMOTE_YJS_ORIGIN);
                applied += 1;
                this.seenOpIds.add(op.opId);
                this.appliedSeq = Math.max(this.appliedSeq, op.seq);
            }
            catch (err) {
                if (typeof console !== "undefined") {
                    console.warn("[Wave Yjs] applyRemoteOps failed", op.opId, op.seq, err);
                }
                failed += 1;
                needReconcile = true;
                continue;
            }
        }
        return { needReconcile, applied, skippedLegacy, failed };
    }
    notifyRealtimeConnectionChange() {
        const connected = this.realtime?.isConnected() ?? false;
        if (connected && !this.wasRealtimeConnected) {
            void this.reconcileWithServer();
        }
        this.wasRealtimeConnected = connected;
        this.updatePollingFallbackFlag();
    }
    markOpsSeen(ops) {
        for (const op of ops) {
            this.seenOpIds.add(op.opId);
            this.appliedSeq = Math.max(this.appliedSeq, op.seq);
        }
    }
    async appendOpsB64(opsB64) {
        if (!this.waveId || !opsB64.length)
            return;
        this.pendingOps.push(...opsB64);
        await this.enqueueDrain();
        if (this.lastEmitError) {
            throw new Error(this.lastEmitError);
        }
    }
    async drainEmit() {
        if (!this.waveId)
            return;
        this.emitDraining = true;
        this.recomputeSaveState();
        try {
            while (this.pendingOps.length > 0) {
                const batch = this.pendingOps.splice(0, this.pendingOps.length);
                try {
                    const result = await WaveCloudAPI.appendOps(this.cfg, this.waveId, batch);
                    this.setEmitError(null);
                    this.pendingDrainAttempt = 0;
                    this.clearPendingDrainTimer();
                    if (result.ops.length) {
                        this.markOpsSeen(result.ops);
                        this.updateServerCursors({ nextOpSeq: result.nextOpSeq });
                    }
                    if (this.pendingOps.length === 0 &&
                        !this.pendingEmit &&
                        !this.hasUnconfirmedLocalDiff()) {
                        this.lastAckAt = Date.now();
                    }
                    this.persistPendingYjsOps();
                    this.persistDocumentCache();
                    this.alignSentStateVectorIfSynced("drain-ack");
                }
                catch (err) {
                    const message = err instanceof Error ? err.message : "Sync wave non riuscita";
                    this.setEmitError(message);
                    this.pendingOps.unshift(...batch);
                    this.persistPendingYjsOps();
                    this.schedulePendingDrainRetry();
                    break;
                }
            }
        }
        finally {
            this.emitDraining = false;
            this.recomputeSaveState();
            if (this.pendingOps.length > 0 && !this.pendingDrainTimer) {
                this.schedulePendingDrainRetry();
            }
        }
    }
    async publishSnapshot(seq, formatVersion) {
        if (!this.waveId || seq <= 0)
            return;
        const snap = encodeFullYDocSnapshotB64(this.doc);
        await WaveCloudAPI.publishSnapshot(this.cfg, this.waveId, snap, seq, formatVersion);
    }
    async upgradeToYjsV1(seq) {
        await this.publishSnapshot(seq, WAVE_FORMAT_YJS_V1);
    }
    setEmitError(message) {
        this.lastEmitError = message;
        if (message == null) {
            this.flushRetryAttempt = 0;
        }
        this.onEmitError?.(message);
        this.recomputeSaveState();
    }
    async startRealtime(waveId) {
        if (!this.realtime) {
            this.startSafetyTimers(waveId);
            return;
        }
        try {
            await this.realtime.connect();
        }
        catch {
            this.startSafetyTimers(waveId);
            return;
        }
        this.unsubscribeRealtime?.();
        this.unsubscribeRealtime = this.realtime.subscribe(WAVE_GRAPHQL.onWaveOps, { waveId }, (batch) => {
            const nextOpSeq = extractNextOpSeqFromSubscriptionPayload(batch);
            if (nextOpSeq != null) {
                this.updateServerCursors({ nextOpSeq });
            }
            const ops = extractWaveOpsFromSubscriptionPayload(batch);
            if (ops.length) {
                this.logSync("subscription ops", `count=${ops.length} seqs=${ops.map((o) => o.seq).join(",")}`);
                this.applyRemoteOps(ops);
            }
            else if (nextOpSeq != null) {
                this.logSync("subscription cursor", `nextOpSeq=${nextOpSeq} no ops`);
                if (this.diagnostics.seqGap > 0) {
                    void this.reconcileWithServer();
                }
            }
        });
        this.wasRealtimeConnected = this.realtime.isConnected();
        this.updatePollingFallbackFlag();
        this.startSafetyTimers(waveId);
    }
    updatePollingFallbackFlag() {
        if (!this.realtime)
            return;
        const wsLive = this.realtime.isConnected();
        const timersActive = this.pollTimer != null || this.reconcileTimer != null;
        this.realtime.setPollingFallbackActive(timersActive && !wsLive);
    }
    async pollOnce(waveId) {
        this.updatePollingFallbackFlag();
        try {
            const ops = await WaveCloudAPI.listAllOps(this.cfg, waveId, this.appliedSeq);
            if (ops.length) {
                this.logSync("poll ops", `count=${ops.length} afterSeq=${this.appliedSeq}`);
                this.applyRemoteOps(ops);
            }
            else if (this.diagnostics.seqGap > 0) {
                this.logSync("poll seq gap", `gap=${this.diagnostics.seqGap} appliedSeq=${this.appliedSeq}`);
                void this.reconcileWithServer();
            }
        }
        catch (err) {
            const message = err instanceof Error ? err.message : "Sync polling non riuscito";
            this.setEmitError(message);
        }
    }
    needsReconcileWork() {
        return (this.pendingEmit ||
            this.hasUnconfirmedLocalDiff() ||
            this.pendingOps.length > 0 ||
            this.diagnostics.seqGap > 0 ||
            this.lastEmitError != null);
    }
    shouldRunSafetyReconcileNow() {
        this.safetyReconcileTick += 1;
        return shouldRunSafetyReconcile({
            needsReconcileWork: this.needsReconcileWork(),
            hasOutboundWork: this.hasOutboundWork(),
            safetyTick: this.safetyReconcileTick,
            idleFallbackEveryNTicks: RECONCILE_IDLE_FALLBACK_TICKS,
        });
    }
    startSafetyTimers(waveId) {
        if (!this.pollTimer) {
            void this.pollOnce(waveId);
            this.pollTimer = setInterval(() => {
                void this.pollOnce(waveId);
            }, POLL_MS);
        }
        if (!this.reconcileTimer) {
            this.safetyReconcileTick = 0;
            this.reconcileTimer = setInterval(() => {
                if (this.shouldRunSafetyReconcileNow()) {
                    void this.reconcileWithServer();
                }
            }, RECONCILE_SAFETY_MS);
        }
        this.updatePollingFallbackFlag();
    }
    dispose() {
        this.doc.off("update", this.handleDocUpdate);
        this.editorBridgeUnsub?.();
        this.editorBridgeUnsub = null;
        this.editorListeners.clear();
        this.clearPendingDrainTimer();
        this.disconnect();
    }
    /** Persist cache when document materialization changes (e.g. after remote merge). */
    notifyDocumentMaterialized() {
        this.emitDiagnostics("materialized");
        this.persistDocumentCache();
    }
}
